'use client';

import { useMemo, type ComponentProps } from 'react';
import type { ShopperProducts } from '@/scapi';
import { WishlistButton } from '@/components/buttons/wishlist-button';
import { toWishlistProductSearchHit } from '@/lib/product-conversion';
import { useOptionalProductView } from '@/providers/product-view';

type WishlistButtonProps = ComponentProps<typeof WishlistButton>;

export type PdpWishlistButtonProps = Omit<WishlistButtonProps, 'product' | 'variant'> & {
    product: ShopperProducts.schemas['Product'];
    /** When set, skips reading the selected variant from ProductView context. */
    variant?: ShopperProducts.schemas['Variant'];
};

function isSamePdpProduct(
    contextProduct: ShopperProducts.schemas['Product'],
    product: ShopperProducts.schemas['Product']
): boolean {
    if (contextProduct.id === product.id) {
        return true;
    }

    const contextMasterId = contextProduct.master?.masterId ?? contextProduct.id;
    const productMasterId = product.master?.masterId ?? product.id;
    return contextMasterId === productMasterId;
}

export function PdpWishlistButton({ product, variant: variantOverride, ...buttonProps }: PdpWishlistButtonProps) {
    const productView = useOptionalProductView();
    const selectedVariant = useMemo(() => {
        if (variantOverride) {
            return variantOverride;
        }
        if (!productView || !isSamePdpProduct(productView.product, product)) {
            return undefined;
        }
        return productView.currentVariant;
    }, [product, productView, variantOverride]);

    const { product: searchHit, variant: variantHit } = useMemo(
        () => toWishlistProductSearchHit(product, selectedVariant),
        [product, selectedVariant]
    );

    return <WishlistButton product={searchHit} variant={variantHit} {...buttonProps} />;
}
